import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react"

import { Sheet, SheetContent } from "@/components/ui/sheet"
import { Slider } from "@/components/ui/slider"

import TonIcon from "@/assets/icons/ton-icon.svg?react"

import ShortButton from "@/shared/ui/ShortButton"
import LargeButton from "@/shared/ui/LargeButton"
import { cn } from "@/shared/lib/utils"
import { formatHundredthsTrimZeros } from "@/shared/lib/pvpRouletteMath"
import { useI18n } from "@/shared/i18n"

import { BetSheetHeader } from "./BetSheetHeader"

const STORAGE_KEY = "pvp-bet-settings-v2"
const PRESETS_COUNT = 3
/** Default quick-pick templates for new users / empty storage (not tied to balance). */
const DEFAULT_PRESET_TEMPLATES: readonly number[] = [0.5, 1, 3]

type TabKey = "setup" | "presets"

export interface BetSettings {
  amount: number
  presets: number[]
}

interface BetSettingsSheetProps {
  roomId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  balanceAmount?: string
  onSettingsChange: (settings: BetSettings) => void
  sectorNowPercent?: number
  sectorAfterPercent?: number
  /** When set, sheet shows one CTA that places with this mode; when null, Solo + Squad. */
  lockedBetMode?: "solo" | "squad" | null
  canPlaceBet?: boolean
  placeBetLoading?: boolean
  onPlaceFromSheet?: (args: {
    settings: BetSettings
    mode: "solo" | "squad"
  }) => void | Promise<void>
}

const quantizeTon = (value: number) => {
  return Math.round(value * 10) / 10
}

const clamp = (value: number, min: number, max: number) => {
  return Math.max(min, Math.min(max, value))
}

const parseBalanceTon = (balanceAmount?: string) => {
  const parsed = Number(balanceAmount ?? "0")
  if (!Number.isFinite(parsed) || parsed <= 0) return 0
  return parsed
}

/** Preset amounts are player-owned; balance only caps the active stake at apply/slider time. */
const normalizePresets = (input: unknown) => {
  if (!Array.isArray(input)) {
    return DEFAULT_PRESET_TEMPLATES.map((t) => quantizeTon(t))
  }
  const prepared = input
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v >= 0)
    .slice(0, PRESETS_COUNT)
    .map((v) => quantizeTon(v))
  while (prepared.length < PRESETS_COUNT) {
    const pad = DEFAULT_PRESET_TEMPLATES[prepared.length] ?? 1
    prepared.push(quantizeTon(pad))
  }
  return prepared
}

/**
 * Default stake: 1 TON if balance ≥ 1, otherwise whole balance (e.g. 0.4 → 0.4).
 * When balance is not known yet (`maxAmount` 0 before API), returns 1 so the panel
 * is not stuck at 0; zero-balance users see the deposit flow instead of betting.
 */
const defaultBetAmountForBalance = (maxAmount: number) => {
  if (maxAmount <= 0) return 1
  if (maxAmount >= 1) return 1
  return maxAmount
}

export const loadBetSettings = (balanceAmount?: string): BetSettings => {
  const maxAmount = parseBalanceTon(balanceAmount)
  const hasKnownBalance = maxAmount > 0
  const fallback = {
    amount: defaultBetAmountForBalance(maxAmount),
    presets: normalizePresets(undefined),
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    const presets = normalizePresets(parsed?.presets)
    const rawAmount = Number(parsed?.amount)
    let amount = Number.isFinite(rawAmount) ? rawAmount : fallback.amount
    if (hasKnownBalance) {
      amount = clamp(amount, 0, maxAmount)
    }
    if (amount <= 0) {
      amount = fallback.amount
    }
    return { amount, presets }
  } catch {
    return fallback
  }
}

export const persistBetSettings = (settings: BetSettings) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
}

/** True if user has saved bet settings (not relying on built-in defaults). */
export const hasBetSettingsInStorage = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) != null
  } catch {
    return false
  }
}

const BetSetupPrimaryFooter = ({
  settings,
  lockedBetMode,
  canPlaceBet,
  placeBetLoading,
  onPlaceFromSheet,
  onOpenChange,
}: {
  settings: BetSettings
  lockedBetMode: "solo" | "squad" | null
  canPlaceBet: boolean
  placeBetLoading: boolean
  onPlaceFromSheet?: (args: {
    settings: BetSettings
    mode: "solo" | "squad"
  }) => void | Promise<void>
  onOpenChange: (open: boolean) => void
}) => {
  const { t } = useI18n()

  const stake = settings.amount
  const amountLabel = formatHundredthsTrimZeros(stake)
  const stakeBlocked = stake <= 0
  const placeDisabled = stakeBlocked || !canPlaceBet

  const handleSheetPlace = async (mode: "solo" | "squad") => {
    if (stakeBlocked) return
    if (!onPlaceFromSheet) {
      onOpenChange(false)
      return
    }
    const snapshot: BetSettings = { ...settings }
    await onPlaceFromSheet({ settings: snapshot, mode })
  }

  if (stakeBlocked) {
    return (
      <LargeButton variant="green" disabled>
        {t("betting.setupBet")}
      </LargeButton>
    )
  }

  if (lockedBetMode) {
    return (
      <LargeButton
        variant={lockedBetMode === "squad" ? "orange" : "green"}
        disabled={placeDisabled}
        isLoading={placeBetLoading}
        onClick={() => void handleSheetPlace(lockedBetMode)}
      >
        {t("betting.addStake")}
        <span>{amountLabel}</span>
        <div className="relative flex size-[19px] shrink-0 items-center justify-center rounded-full bg-black">
          <TonIcon className="absolute left-1/2 top-0 size-5 -translate-x-1/2 -translate-y-px drop-shadow-[0_-1px_0_rgb(0,0,0)]" />
        </div>
      </LargeButton>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <ShortButton
        variant="green"
        aria-label={t("betting.ariaSoloBet")}
        disabled={placeDisabled}
        isLoading={placeBetLoading}
        onClick={() => void handleSheetPlace("solo")}
      >
        {t("betting.solo")}
        <span>{amountLabel}</span>
        <div className="relative flex size-[19px] shrink-0 items-center justify-center rounded-full bg-black">
          <TonIcon className="absolute left-1/2 top-0 size-5 -translate-x-1/2 -translate-y-px drop-shadow-[0_-1px_0_rgb(0,0,0)]" />
        </div>
      </ShortButton>
      <ShortButton
        variant="orange"
        aria-label={t("betting.ariaSquadBet")}
        disabled={placeDisabled}
        isLoading={placeBetLoading}
        onClick={() => void handleSheetPlace("squad")}
      >
        {t("betting.squad")}
        <span>{amountLabel}</span>
        <div className="relative flex size-[19px] shrink-0 items-center justify-center rounded-full bg-black">
          <TonIcon className="absolute left-1/2 top-0 size-5 -translate-x-1/2 -translate-y-px drop-shadow-[0_-1px_0_rgb(0,0,0)]" />
        </div>
      </ShortButton>
    </div>
  )
}

export const BetSettingsSheet = ({
  roomId,
  open,
  onOpenChange,
  balanceAmount,
  onSettingsChange,
  sectorNowPercent = 0,
  sectorAfterPercent = 0,
  lockedBetMode = null,
  canPlaceBet = false,
  placeBetLoading = false,
  onPlaceFromSheet,
}: BetSettingsSheetProps) => {
  const { t } = useI18n()
  const maxAmount = parseBalanceTon(balanceAmount)
  const [settings, setSettings] = useState<BetSettings>(() =>
    loadBetSettings(balanceAmount)
  )
  const [tab, setTab] = useState<TabKey>("setup")
  const [isEditingPresets, setIsEditingPresets] = useState(false)
  const [editingPresets, setEditingPresets] = useState<string[]>([])
  const amountInputRef = useRef<HTMLInputElement>(null)
  const [amountInputValue, setAmountInputValue] = useState<string>(() =>
    formatHundredthsTrimZeros(settings.amount)
  )
  const [isAmountInputFocused, setIsAmountInputFocused] = useState(false)

  useEffect(() => {
    if (open) {
      const loaded = loadBetSettings(balanceAmount)
      setSettings(loaded)
      setTab("setup")
      setIsEditingPresets(false)
      setEditingPresets(loaded.presets.map((v) => formatHundredthsTrimZeros(v)))
      setAmountInputValue(formatHundredthsTrimZeros(loaded.amount))
      setIsAmountInputFocused(false)
    }
  }, [open, balanceAmount])

  useEffect(() => {
    if (!isAmountInputFocused) {
      setAmountInputValue(formatHundredthsTrimZeros(settings.amount))
    }
  }, [settings.amount, isAmountInputFocused])

  const maxLabel = useMemo(() => formatHundredthsTrimZeros(maxAmount), [maxAmount])

  const update = (patch: Partial<BetSettings>) => {
    const next = {
      ...settings,
      ...patch,
    }
    const normalized: BetSettings = {
      amount: clamp(next.amount, 0, maxAmount),
      presets: normalizePresets(next.presets),
    }
    setSettings(normalized)
    persistBetSettings(normalized)
    onSettingsChange(normalized)
  }

  const handleTogglePresetEditing = () => {
    if (!isEditingPresets) {
      setEditingPresets(settings.presets.map((v) => formatHundredthsTrimZeros(v)))
      setIsEditingPresets(true)
      return
    }
    const nextPresets = editingPresets.map((raw, idx) => {
      const n = Number(raw.replace(",", "."))
      if (!Number.isFinite(n)) return settings.presets[idx] ?? 0
      return Math.max(0, quantizeTon(n))
    })
    update({ presets: nextPresets })
    setIsEditingPresets(false)
  }

  const focusAmountInput = () => {
    const input = amountInputRef.current
    if (!input) return
    try {
      input.focus({ preventScroll: true })
    } catch {
      input.focus()
    }
  }

  const handleAmountInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const normalized = e.target.value.replace(",", ".").replace(/[^\d.]/g, "")
    const [intPartRaw, decimalPart] = normalized.split(".")
    const intPartTrimmed =
      intPartRaw.replace(/^0+(\d)/, "$1").slice(0, 6) ||
      (intPartRaw.length ? "0" : "")
    const hasDot = normalized.includes(".")
    const sanitized = hasDot
      ? `${intPartTrimmed}.${(decimalPart ?? "").slice(0, 1)}`
      : intPartTrimmed

    if (sanitized === "" || sanitized === "." || sanitized === "0.") {
      setAmountInputValue(sanitized)
      if (sanitized === "") update({ amount: 0 })
      return
    }

    const parsed = Number(sanitized)
    if (!Number.isFinite(parsed)) {
      setAmountInputValue("")
      return
    }

    const clamped = clamp(quantizeTon(parsed), 0, maxAmount)
    setAmountInputValue(sanitized)
    update({ amount: clamped })
  }

  const handleAmountInputBlur = () => {
    setIsAmountInputFocused(false)
    if (
      amountInputValue === "" ||
      amountInputValue === "." ||
      amountInputValue === "0."
    ) {
      update({ amount: 0 })
      setAmountInputValue("0")
      return
    }
    const parsed = Number(amountInputValue)
    if (!Number.isFinite(parsed)) {
      setAmountInputValue(formatHundredthsTrimZeros(settings.amount))
      return
    }
    const clamped = clamp(quantizeTon(parsed), 0, maxAmount)
    update({ amount: clamped })
    setAmountInputValue(formatHundredthsTrimZeros(clamped))
  }

  const handleAmountInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      amountInputRef.current?.blur()
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="overflow-hidden rounded-t-[24px] border-none p-0"
        closeClassName="top-4 right-2"
      >
        <div className="w-full bg-sheet">
          <BetSheetHeader
            roomId={roomId}
            sectorNowPercent={sectorNowPercent ?? 0}
            sectorAfterPercent={sectorAfterPercent ?? 0}
          />

          <div className="mb-8 mt-4 px-4">
            <div className="flex h-[35px] items-center gap-0.5 rounded-full bg-gray-32 p-0.5">
              <button
                type="button"
                onClick={() => setTab("setup")}
                className={cn(
                  "h-[31px] flex-1 rounded-full text-base tracking-[-0.5px] transition-colors",
                  tab === "setup"
                    ? "bg-gray-100 font-medium text-white"
                    : "font-medium text-white/40"
                )}
              >
                {t("betting.sheetSetupTab")}
              </button>
              <button
                type="button"
                onClick={() => setTab("presets")}
                className={cn(
                  "h-[31px] flex-1 rounded-full text-base tracking-[-0.5px] transition-colors",
                  tab === "presets"
                    ? "bg-gray-100 font-medium text-white"
                    : "font-medium text-white/40"
                )}
              >
                {t("betting.sheetPresetsTab")}
              </button>
            </div>
          </div>

          {tab === "setup" ? (
            <div className="h-32 px-4">
              <div
                data-amount-field
                className="relative mb-2 inline-flex max-w-full cursor-text items-center gap-0.5"
                onClick={focusAmountInput}
              >
                <input
                  ref={amountInputRef}
                  type="text"
                  inputMode="decimal"
                  enterKeyHint="done"
                  value={amountInputValue}
                  onFocus={() => setIsAmountInputFocused(true)}
                  onBlur={handleAmountInputBlur}
                  onChange={handleAmountInputChange}
                  onKeyDown={handleAmountInputKeyDown}
                  aria-label={t("betting.ariaBetAmount")}
                  className="pointer-events-none absolute left-1/2 top-1/2 h-px w-px -translate-x-1/2 -translate-y-1/2 opacity-0"
                />
                <span className="text-4xl tabular-nums tracking-[-0.5px] text-white">
                  {isAmountInputFocused
                    ? amountInputValue || "0"
                    : formatHundredthsTrimZeros(settings.amount)}
                </span>
                {isAmountInputFocused && (
                  <span
                    aria-hidden
                    className="mx-0.5 h-11 w-0.5 animate-pulse rounded-full bg-[#B1FE64]"
                  />
                )}
                <span className="text-4xl tracking-[-0.5px] text-white/70">
                  TON
                </span>
              </div>
              <div className="flex h-[52px] items-center gap-5 pl-2">
                <Slider
                  value={[settings.amount]}
                  max={maxAmount}
                  min={0}
                  step={0.1}
                  onValueChange={(value) => update({ amount: value[0] ?? 0 })}
                  className="flex-1"
                />
                <div
                  className="w-10 text-right text-base font-semibold tracking-[-0.5px] text-white"
                  onClick={() => update({ amount: maxAmount })}
                >
                  {maxLabel}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-32 px-4">
              <div className="mb-[26px] text-[24px] font-semibold leading-[29px] tracking-[-0.5px] text-white">
                TON
              </div>
              <div className="mb-[6px]">
                <div className="grid grid-cols-3 gap-2">
                  {settings.presets.map((preset, idx) =>
                    isEditingPresets ? (
                      <input
                        key={`preset-input-${idx}`}
                        type="text"
                        inputMode="decimal"
                        value={editingPresets[idx] ?? ""}
                        onChange={(e) => {
                          const next = [...editingPresets]
                          const normalized = e.target.value
                            .replace(",", ".")
                            .replace(/[^\d.]/g, "")
                          const [intPartRaw, decimalPart] =
                            normalized.split(".")
                          const intPartTrimmed =
                            intPartRaw.replace(/^0+(\d)/, "$1").slice(0, 6) ||
                            (intPartRaw.length ? "0" : "")
                          const hasDot = normalized.includes(".")
                          const sanitized = hasDot
                            ? `${intPartTrimmed}.${(decimalPart ?? "").slice(0, 1)}`
                            : intPartTrimmed
                          next[idx] = sanitized
                          setEditingPresets(next)
                        }}
                        className="h-[43px] rounded-2xl bg-gray-32 text-center text-base font-medium leading-[19px] tracking-[-0.5px] text-white outline-none ring-1 ring-gray-100/70 [appearance:textfield] focus:ring-gray-100 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                    ) : (
                      <button
                        key={`preset-${idx}`}
                        type="button"
                        onClick={() => update({ amount: preset })}
                        className="h-[43px] rounded-2xl bg-gray-32 text-base font-medium leading-[19px] tracking-[-0.5px] text-white"
                      >
                        {formatHundredthsTrimZeros(preset)}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="px-4 pb-[calc(1rem+var(--app-safe-bottom))] pt-2">
            {tab === "presets" ? (
              <LargeButton onClick={handleTogglePresetEditing}>
                {isEditingPresets ? "Save presets" : "Edit presets"}
              </LargeButton>
            ) : (
              <BetSetupPrimaryFooter
                settings={settings}
                lockedBetMode={lockedBetMode}
                canPlaceBet={canPlaceBet}
                placeBetLoading={placeBetLoading}
                onPlaceFromSheet={onPlaceFromSheet}
                onOpenChange={onOpenChange}
              />
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
